import { faEdit, faExternalLinkAlt, faEye, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Badge, Button, Tooltip } from '@ui';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { fileIcon, formatFileSize } from './fileHelpers';
import FileValidityBadge from './FileValidityBadge';
import { MemberFile } from './types';

interface Props {
    file: MemberFile;
    onOpen: (file: MemberFile) => void;
    onEdit?: (file: MemberFile) => void;
    onDelete?: (file: MemberFile) => void;
    /** The club's view shows source and visibility, the member's own view does not. */
    showClubDetails?: boolean;
}

export const MemberFileRow = ({ file, onOpen, onEdit, onDelete, showClubDetails }: Props) => {
    const { t } = useTranslation('member-management');

    const meta = [
        file.name,
        formatFileSize(file.size),
        moment(file.created_at).format('DD.MM.YYYY'),
        showClubDetails && file.uploaded_by
            ? t('clubMember.files.meta.uploadedBy', { name: file.uploaded_by.name })
            : null
    ].filter(Boolean);

    return (
        <div className="tw-flex tw-items-center tw-gap-3 tw-border-0 tw-border-b tw-border-solid tw-border-accents-2 tw-py-2.5 last:tw-border-b-0">
            <button
                type="button"
                onClick={() => onOpen(file)}
                className="tw-flex tw-h-10 tw-w-10 tw-flex-none tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-solid tw-border-accents-2 tw-bg-accents-0 tw-text-accents-7"
                aria-label={t('clubMember.files.actions.open')}
            >
                <FontAwesomeIcon icon={fileIcon(file.mime_type)} />
            </button>
            <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-1">
                <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-1.5">
                    <button
                        type="button"
                        onClick={() => onOpen(file)}
                        className="tw-truncate tw-border-0 tw-bg-transparent tw-p-0 tw-text-left tw-text-sm tw-font-semibold tw-text-accents-9 hover:tw-underline"
                    >
                        {file.display_title}
                    </button>
                    {showClubDetails && file.source && (
                        <Badge size="xs" color="light">
                            {t(`clubMember.files.source.${file.source}`)}
                        </Badge>
                    )}
                    {showClubDetails && file.visible_to_member && (
                        <Badge size="xs" color="success" flat>
                            <FontAwesomeIcon icon={faEye} className="tw-mr-1" />
                            {t('clubMember.files.badges.visibleToMember')}
                        </Badge>
                    )}
                    {file.valid_until && (
                        <Badge size="xs" color="light" flat>
                            {t('clubMember.files.badges.validUntil', {
                                date: moment(file.valid_until).format('DD.MM.YYYY')
                            })}
                        </Badge>
                    )}
                    <FileValidityBadge validity={file.validity} />
                </div>
                <span className="tw-truncate tw-text-xs tw-text-accents-6">{meta.join(' · ')}</span>
            </div>
            <div className="tw-flex tw-flex-none tw-gap-1">
                <Tooltip placement="top" tooltip={t('clubMember.files.actions.open')}>
                    <Button size="xs" flat color="light" onPress={() => onOpen(file)}>
                        <FontAwesomeIcon icon={faExternalLinkAlt} />
                    </Button>
                </Tooltip>
                {onEdit && (
                    <Tooltip placement="top" tooltip={t('clubMember.files.actions.edit')}>
                        <Button size="xs" flat color="warning" onPress={() => onEdit(file)}>
                            <FontAwesomeIcon icon={faEdit} />
                        </Button>
                    </Tooltip>
                )}
                {onDelete && (
                    <Tooltip placement="top" tooltip={t('clubMember.files.actions.delete')}>
                        <Button size="xs" flat color="error" onPress={() => onDelete(file)}>
                            <FontAwesomeIcon icon={faTrash} />
                        </Button>
                    </Tooltip>
                )}
            </div>
        </div>
    );
};

export default MemberFileRow;
